import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  getValorcarCategories,
  getValorcarReport,
} from "../../services/valorcarServices";

const MONTH_LABELS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const ValorcarReport = () => {
  const { t } = useTranslation();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [period, setPeriod] = useState("month"); // "month" | "quarter"
  const [subPeriod, setSubPeriod] = useState(""); // "" all, ou "1".."12" (mes) ou "1".."4" (trim)
  const [categoryId, setCategoryId] = useState("");

  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCategories = async () => {
    try {
      const r = await getValorcarCategories();
      setCategories(r.data?.data || []);
    } catch (e) {
      setCategories([]);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = {
        year,
        period,
        category_id: categoryId || undefined,
      };
      if (period === "month" && subPeriod) params.month = subPeriod;
      if (period === "quarter" && subPeriod) params.quarter = subPeriod;
      const r = await getValorcarReport(params);
      setRows(r.data?.data?.rows || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // Reset subPeriod ao trocar period (Q1 não faz sentido em "mensal").
  useEffect(() => {
    setSubPeriod("");
  }, [period]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, period, subPeriod, categoryId]);

  // Lista canónica de buckets que SEMPRE aparecem (mesmo a 0):
  const expectedBuckets = useMemo(() => {
    if (period === "month") {
      if (subPeriod) {
        const mm = String(subPeriod).padStart(2, "0");
        return [`${year}-${mm}`];
      }
      return Array.from({ length: 12 }, (_, i) =>
        `${year}-${String(i + 1).padStart(2, "0")}`
      );
    }
    // quarter
    if (subPeriod) return [`${year}-Q${subPeriod}`];
    return [1, 2, 3, 4].map((q) => `${year}-Q${q}`);
  }, [year, period, subPeriod]);

  const bucketLabel = (b) => {
    if (b.includes("-Q")) return b.split("-Q").join(" Q");
    const mm = parseInt(b.split("-")[1], 10);
    return MONTH_LABELS[mm - 1] || b;
  };

  // Pivot: catId -> bucket -> {qty_out, qty_in, qty_net}
  const cell = useMemo(() => {
    const c = {};
    for (const r of rows) {
      const k = `${r.bucket}:${r.valorcar_category_id}`;
      c[k] = {
        qty_out: r.qty_out || 0,
        qty_in: r.qty_in || 0,
        qty_net: r.qty_net || 0,
      };
    }
    return c;
  }, [rows]);

  // Categorias mostradas: sempre TODAS (mesmo a 0). Filtro de categoria
  // restringe se aplicado.
  const shownCategories = useMemo(() => {
    if (!categoryId) return categories;
    return categories.filter((c) => c.id === parseInt(categoryId, 10));
  }, [categories, categoryId]);

  const exportCSV = () => {
    const lines = [];
    const header = [
      "Categoria",
      ...expectedBuckets.map(bucketLabel),
      "Total líquido",
    ];
    lines.push(header.join(";"));
    for (const cat of shownCategories) {
      const row = [cat.description_pt];
      let total = 0;
      for (const b of expectedBuckets) {
        const c = cell[`${b}:${cat.id}`];
        const net = c ? c.qty_net : 0;
        row.push(net);
        total += net;
      }
      row.push(total);
      lines.push(row.join(";"));
    }
    const subLabel =
      period === "month" && subPeriod
        ? `_${MONTH_LABELS[parseInt(subPeriod, 10) - 1]?.toLowerCase() || subPeriod}`
        : period === "quarter" && subPeriod
        ? `_Q${subPeriod}`
        : "";
    const blob = new Blob(["﻿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `valorcar_${year}_${period}${subLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const subPeriodOptions = useMemo(() => {
    if (period === "month") {
      return [
        { v: "", label: "Todos os meses" },
        ...MONTH_LABELS.map((m, i) => ({ v: String(i + 1), label: m })),
      ];
    }
    return [
      { v: "", label: "Todos os trimestres" },
      { v: "1", label: "Q1 (Jan–Mar)" },
      { v: "2", label: "Q2 (Abr–Jun)" },
      { v: "3", label: "Q3 (Jul–Set)" },
      { v: "4", label: "Q4 (Out–Dez)" },
    ];
  }, [period]);

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <Row className="align-items-center">
            <Col>
              <h1>{t("valorcar_reports", { defaultValue: "Relatórios Valorcar" })}</h1>
            </Col>
            <Col className="text-end">
              <Link to="/valorcar">
                <Button color="light">
                  <i className="bi bi-arrow-left me-1"></i> Voltar
                </Button>
              </Link>
            </Col>
          </Row>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <Card className="mb-3">
            <CardBody>
              <Row>
                <Col md={2}>
                  <FormGroup>
                    <Label>Ano</Label>
                    <Input
                      type="number"
                      min={2020}
                      max={currentYear + 1}
                      value={year}
                      onChange={(e) =>
                        setYear(parseInt(e.target.value, 10) || currentYear)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label>Período</Label>
                    <Input
                      type="select"
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                    >
                      <option value="month">Mensal</option>
                      <option value="quarter">Trimestral</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>{period === "month" ? "Mês" : "Trimestre"}</Label>
                    <Input
                      type="select"
                      value={subPeriod}
                      onChange={(e) => setSubPeriod(e.target.value)}
                    >
                      {subPeriodOptions.map((s) => (
                        <option key={s.v} value={s.v}>{s.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Categoria</Label>
                    <Input
                      type="select"
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                    >
                      <option value="">Todas</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.description_pt}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    color="success"
                    onClick={exportCSV}
                    className="w-100 mb-3"
                    disabled={shownCategories.length === 0}
                  >
                    <i className="bi bi-download me-1"></i> CSV
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center my-4"><Spinner /></div>
              ) : (
                <>
                  {rows.length === 0 && (
                    <Alert color="info" className="mb-3">
                      Sem dados para os filtros seleccionados. A tabela mostra todas as categorias a 0 — útil para enviar mapas zerados à Valorcar.
                    </Alert>
                  )}
                  <Table responsive striped bordered>
                    <thead>
                      <tr>
                        <th>Categoria</th>
                        {expectedBuckets.map((b) => (
                          <th key={b} className="text-center">{bucketLabel(b)}</th>
                        ))}
                        <th className="text-center">Total líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shownCategories.map((cat) => {
                        let total = 0;
                        return (
                          <tr key={cat.id}>
                            <td><strong>{cat.description_pt}</strong></td>
                            {expectedBuckets.map((b) => {
                              const c = cell[`${b}:${cat.id}`];
                              const net = c ? c.qty_net : 0;
                              total += net;
                              return (
                                <td key={b} className="text-center">
                                  {c ? (
                                    <span title={`Saídas: ${c.qty_out} · Entradas: ${c.qty_in}`}>
                                      {net}
                                    </span>
                                  ) : (
                                    <span className="text-muted">0</span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="text-center"><strong>{total}</strong></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
            </CardBody>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ValorcarReport;
